import { useState, FormEvent, useEffect } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

const stats = [
  { value: '10,000+', label: '正版漫画' },
  { value: '500万+', label: '活跃读者' },
  { value: '99.9%', label: '高清画质' },
  { value: '50+', label: '合作出版社' },
];

const milestones = [
  { year: '2022', title: '平台成立', desc: '琉璃漫站正式上线，专注正版漫画在线阅读' },
  { year: '2023', title: '资源拓展', desc: '与国内外50+出版社达成合作，漫画数量突破5000部' },
  { year: '2024', title: '用户突破', desc: '注册用户突破300万，月活读者超过150万' },
  { year: '2025', title: '技术升级', desc: '推出AI智能推荐系统，阅读体验全面升级' },
  { year: '2026', title: '全新启航', desc: '平台全面改版，新增韩漫频道，漫画总量突破10000部' },
];

export default function About() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 琉璃漫站 | 正版漫画在线阅读平台';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '了解琉璃漫站——专注于正版漫画在线阅读的综合平台。查看品牌介绍、发展历程、用户口碑与联系方式。琉璃漫站汇聚海量国漫、日漫、韩漫资源，提供高清流畅阅读体验。');
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', '琉璃漫站,关于琉璃漫站,漫画阅读平台,正版漫画平台,漫画网站');
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    const honeypotVal = honeypot?.value?.trim();

    if (honeypotVal) {
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    if (formData.message.length > 500) {
      setErrorMsg('留言内容不能超过500个字符');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const payload = new FormData(form);
      payload.delete('phone_alt');

      const res = await fetch('https://readdy.ai/api/form/d9rh4k4nlsngrm4lmc40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        if (msg.includes('spam')) {
          setStatus('success');
          setFormData({ name: '', email: '', subject: '', message: '' });
          return;
        }
        setErrorMsg(msg);
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请检查连接后重试');
      setStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '琉璃漫站',
    url: 'https://liulicomic.com',
    description: '琉璃漫站是专注于正版漫画在线阅读的综合平台，汇聚海量国漫、日漫、韩漫资源，提供高清流畅的阅读体验。',
    email: 'contact@liulicomic.com',
    telephone: '+86-400-888-6666',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
      addressRegion: '北京市',
      streetAddress: '北京市朝阳区望京科技园',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '琉璃漫站的漫画是否都是正版？',
        acceptedAnswer: { '@type': 'Answer', text: '是的，琉璃漫站所有漫画均获得正版授权，与国内外50余家出版社达成合作，确保每一部漫画都是正版内容，让读者阅读更安心。' },
      },
      {
        '@type': 'Question', 
        name: '免费用户可以阅读多少内容？',
        acceptedAnswer: { '@type': 'Answer', text: '琉璃漫站提供大量免费漫画内容，免费用户每天可以阅读一定章节。成为会员后可享受无限阅读、去广告、高清画质等更多权益。' },
      },
      {
        '@type': 'Question',
        name: '琉璃漫站支持哪些设备阅读？',
        acceptedAnswer: { '@type': 'Answer', text: '琉璃漫站支持PC网页端、平板电脑和手机移动端阅读。我们采用响应式设计，自动适配不同屏幕尺寸，让你随时随地畅享阅读。' },
      },
      {
        '@type': 'Question',
        name: '如何订阅琉璃漫站的最新更新通知？',
        acceptedAnswer: { '@type': 'Answer', text: '你可以在首页底部输入邮箱地址订阅我们的通知服务，第一时间获取最新漫画更新、独家活动和限时免费阅读资讯。' },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

          {/* Hero */}
          <section className="py-12 md:py-20 text-center">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-5">
              关于琉璃漫站
            </h1>
            <p className="text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              琉璃漫站诞生于对漫画的热爱。我们相信，每一部漫画都是一个世界，
              每一次翻页都是一次冒险。平台汇聚海量正版漫画资源，
              致力于为中国漫画爱好者提供最优质的在线阅读体验。
            </p>
          </section>

          {/* Stats */}
          <section className="pb-14 md:pb-20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {stats.map((stat) => (
                <div key={stat.label} className="p-6 rounded-xl bg-background-100 text-center">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-600">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-600 mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Brand Story */}
          <section className="pb-14 md:pb-20">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              品牌故事
            </h2>
            <p className="text-foreground-600 text-center mb-10 max-w-xl mx-auto">
              从零开始，一步一个脚印走到今天
            </p>

            <div className="max-w-3xl mx-auto">
              {milestones.map((m, index) => (
                <div key={m.year} className="flex gap-6 pb-10 relative">
                  {/* Timeline line */}
                  {index < milestones.length - 1 && (
                    <div className="absolute left-[27px] top-14 bottom-0 w-px bg-background-200"></div>
                  )}
                  {/* Year badge */}
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-heading font-bold text-sm">
                    {m.year}
                  </div>
                  {/* Content */}
                  <div className="pt-3">
                    <h3 className="font-heading font-bold text-foreground-900 text-lg mb-1">
                      {m.title}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {m.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="pb-14 md:pb-20">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqSchema.mainEntity.map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-100 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="font-heading font-bold text-foreground-900 text-base pr-4">
                      {faq.name}
                    </span>
                    <i className="ri-add-line text-foreground-500 group-open:hidden text-lg flex-shrink-0"></i>
                    <i className="ri-subtract-line text-foreground-500 hidden group-open:block text-lg flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="pb-16 md:pb-20">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-center mb-10">
              有任何问题或建议，欢迎随时联系我们
            </p>

            <div className="flex flex-col lg:flex-row gap-10 max-w-5xl mx-auto">
              {/* Contact Info */}
              <div className="lg:w-[360px] flex-shrink-0 space-y-5">
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h4 className="font-heading font-bold text-foreground-900 mb-1">电子邮箱</h4>
                  <p className="text-sm text-foreground-600">contact@liulicomic.com</p>
                </div>
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center mb-3">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <h4 className="font-heading font-bold text-foreground-900 mb-1">客服电话</h4>
                  <p className="text-sm text-foreground-600">400-888-6666</p>
                </div>
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center mb-3">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <h4 className="font-heading font-bold text-foreground-900 mb-1">公司地址</h4>
                  <p className="text-sm text-foreground-600">北京市朝阳区望京科技园</p>
                </div>
                <div className="p-5 rounded-xl bg-background-100">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mb-3">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <h4 className="font-heading font-bold text-foreground-900 mb-1">工作时间</h4>
                  <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                </div>
              </div>

              {/* Contact Form */}
              <div className="flex-1">
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="p-6 md:p-8 rounded-xl bg-background-100 space-y-5"
                >
                  <input
                    type="text"
                    name="phone_alt"
                    className="website-alt-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-2">
                      主题 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                      留言 <span className="text-primary-500">*</span>
                      <span className="text-foreground-400 font-normal ml-1">（限500字）</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入留言内容"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full py-3.5 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-200 disabled:opacity-60 whitespace-nowrap"
                  >
                    {status === 'loading' ? '发送中...' : '发送留言'}
                  </button>
                  {status === 'success' && (
                    <p className="text-center text-accent-600 text-sm font-medium">留言发送成功！我们会尽快回复你</p>
                  )}
                  {status === 'error' && errorMsg && (
                    <p className="text-center text-red-500 text-sm">{errorMsg}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}